// public/v3/src/lib/pain-billing.js — Chronic Pain Management (G3002/G3003).
//
// CPM is a CONCURRENT slot, not a base program: G3002 is billable in the same
// month as CCM, BHI, and APCM when its time is distinct (it is NOT in the
// APCM bundle). It rides the existing combined bill the way the APCM+G0570
// pairing does — getCcmBillingCodes appends G3002 (+G3003 units) when the
// month's pain gates pass, so no new push lane or idempotency slot exists.
//
// Enrollment lives at billing.painProgram ('G3002') plus the persisted CPM
// record at raw.pain (P0.2): dedicated consent w/ CMS disclosures + >3-month
// duration attestation, the initiating in-person visit (date+practitioner),
// and the DESIGNATED billing provider. CPM is provider-self-service: the
// designated provider logs their own minutes from their own session, and
// only those minutes bill (enforce-from-day-one, 2026-07-20 — decided with
// zero patients enrolled, so no observe mode ever existed).
//
// Minutes are disjoint by construction: entryProgramFamily (bhi-billing.js)
// maps G3002/G3003-tagged entries to the 'pain' family, which every CCM/BHI
// minute helper already excludes.
//
// Module format: CommonJS (pure functions, Jest-requirable) — same pattern
// as bhi-billing.js.

const { entryProgramFamily } = require('./bhi-billing');
const { painAssessmentsForMonth, lookupPainScale, isValidPainAssessment } = require('./pain-scales');

// CPM's authorized-provider list per the CMS CPM rule (NACHC Mar 2026):
// MD/DO/NP/PA/CNM. Deliberately NOT ccm-tier-bump.js's PROVIDER_CREDENTIALS
// (that set serves 99491 and includes CNS, which is not CPM-authorized here).
const CPM_BILLING_CREDENTIALS = new Set(['MD', 'DO', 'NP', 'PA', 'CNM']);

const PAIN_BASE_CODE = 'G3002';
const PAIN_ADDON_CODE = 'G3003';
const PAIN_BASE_TARGET_MIN = 30; // first 30 practitioner minutes / month
const PAIN_ADDON_UNIT_MIN = 15;  // each additional 15 minutes
const PAIN_ADDON_MAX_UNITS = 4;  // mirrors ADDON_CONFIG {base G3002, max 4}

function _raw(patient) {
  return patient && patient._raw ? patient._raw : (patient || {});
}

function _painDeliveryMode(entry) {
  const mode = entry && entry.mode;
  return mode === 'in-person' || mode === 'telehealth' ? mode : '';
}

function isPainEnrolledPatient(patient) {
  const p = String((_raw(patient).billing || {}).painProgram || '').trim();
  return !!p && p.toLowerCase() !== 'none';
}

// Pain-family minutes for the month (G3002/G3003-tagged entries only).
function painMonthlyMinutes(patient, monthStr) {
  const log = Array.isArray(_raw(patient).activityLog) ? _raw(patient).activityLog : [];
  return log
    .filter((e) => e && !e.isConsolidatedNote && (e.date || '').startsWith(monthStr || '') && entryProgramFamily(e) === 'pain')
    .reduce((acc, e) => acc + (parseInt(e.duration, 10) || 0), 0);
}

// Entry credential: prefer the stamped entry.credential (buildEntry, new),
// else resolve via stable staffUserId, else legacy name match. 'none' = unknown.
function _painEntryCredential(entry, staffList) {
  if (entry && entry.credential) return entry.credential;
  const list = Array.isArray(staffList) ? staffList : [];
  const byId = entry && entry.staffUserId
    ? list.find((s) => s && String(s.id) === String(entry.staffUserId)) : null;
  if (byId && byId.credential) return byId.credential;
  const name = String((entry && (entry.staff || entry.staffName)) || '').trim().toLowerCase();
  const byName = name ? list.find((s) => String((s && s.name) || '').toLowerCase() === name) : null;
  return (byName && byName.credential) || 'none';
}

// Resolve an entry's stable staff id for the designated-provider check: prefer
// the stamped staffUserId; otherwise a UNIQUE case-insensitive staff-name match
// against the staff list. Returns null on NO match OR an AMBIGUOUS (2+ same-name
// staff) match — fail-closed, so a legacy name-only entry counts as
// designated-provider time only when the name maps to exactly one staff id
// (codex finding 2). Without this, pre-stamping entries (no staffUserId) could
// never be designated-provider time under enforcement, wrongly zeroing valid
// billable minutes.
function _resolveEntryStaffId(entry, staffList) {
  if (entry && entry.staffUserId) return String(entry.staffUserId);
  const list = Array.isArray(staffList) ? staffList : [];
  const name = String((entry && (entry.staff || entry.staffName)) || '').trim().toLowerCase();
  if (!name) return null;
  const matches = list.filter((s) => String((s && s.name) || '').trim().toLowerCase() === name);
  return matches.length === 1 ? String(matches[0].id) : null;
}

// ONE shared monthly CPM evidence object — gates, code resolution, the pain
// lens UI, and the billing note ALL consume this so they can never disagree
// (codex review 2026-07-07). CPM time counts ONLY when personally furnished
// by the DESIGNATED billing provider (pain.billingProvider): pooled provider
// time (MD A + NP B) must not reach 30 minutes, and staff/unknown-credential
// minutes are excluded fail-closed. There is NO observe mode — enforcement
// was decided from day one (2026-07-20) while zero patients were enrolled,
// so no legacy billing existed to migrate. Billability is CREDENTIAL + the
// designated-provider match, never the user's ROLE: an admin who is also a
// credentialed provider bills exactly like a plain provider.
function cpmMonthlyEvidence(patient, monthStr, staffList) {
  const raw = _raw(patient);
  const designated = (raw.pain && raw.pain.billingProvider) || null;
  const designatedProviderId = designated && designated.id != null && designated.id !== ''
    ? String(designated.id) : null;
  const log = Array.isArray(raw.activityLog) ? raw.activityLog : [];
  let billableProviderMinutes = 0, otherMinutes = 0, unknownMinutes = 0, totalPainMinutes = 0;
  for (const e of log) {
    if (!e || e.isConsolidatedNote || !(e.date || '').startsWith(monthStr || '')) continue;
    if (entryProgramFamily(e) !== 'pain') continue;
    const dur = parseInt(e.duration, 10) || 0;
    totalPainMinutes += dur;
    const cred = _painEntryCredential(e, staffList);
    // Attribute the entry to a stable staff id (staffUserId or a unique
    // staff-name match); only then can it be the DESIGNATED provider's time.
    // No/ambiguous attribution → not designated (codex finding 2).
    const resolvedStaffId = _resolveEntryStaffId(e, staffList);
    const isDesignated = !!designatedProviderId && !!resolvedStaffId && resolvedStaffId === designatedProviderId;
    if (cred === 'none') unknownMinutes += dur;                       // fail-closed: never billable
    else if (CPM_BILLING_CREDENTIALS.has(cred) && isDesignated) billableProviderMinutes += dur;
    else otherMinutes += dur;
  }
  return { billableProviderMinutes, otherMinutes, unknownMinutes, totalPainMinutes, designatedProviderId };
}

// Minutes that count toward G3002/G3003 — always the designated provider's
// personal time (enforce-from-day-one; no family-total fallback exists).
function cpmBillableMinutes(patient, monthStr, staffList) {
  return cpmMonthlyEvidence(patient, monthStr, staffList).billableProviderMinutes;
}

const PAIN_NECESSITY_DESCRIPTION_RE = /pain|arthrit|neuralg|neuropath|fibromyalg|migraine|headache/i;

// Draft text is chart-derived only; it cannot reach a billing note until the
// CPM record dialog stores the provider-confirmed version in raw.pain.
function buildPainNecessityDraft(raw) {
  const conditions = raw && Array.isArray(raw.conditions) ? raw.conditions : [];
  const descriptions = conditions
    .filter((condition) => {
      if (!condition || typeof condition !== 'object') return false;
      const codes = Array.isArray(condition.icd10) ? condition.icd10 : [condition.icd10];
      const codedPain = codes.some((code) => {
        const normalized = String(code || '').trim().toUpperCase();
        return normalized.startsWith('M') || normalized.startsWith('G89');
      });
      return codedPain || PAIN_NECESSITY_DESCRIPTION_RE.test(String(condition.description || ''));
    })
    .map((condition) => String(condition.description || '').trim())
    .filter(Boolean);
  if (!descriptions.length) {
    return 'Chronic pain persisting longer than 3 months and requiring ongoing pain-management services.';
  }
  return 'Chronic pain due to ' + descriptions.join(', ')
    + ', persisting longer than 3 months and requiring ongoing pain-management services.';
}

// True when the persisted CPM record can support billing G3002: dedicated
// consent signed (with the >3-month chronic-pain-duration attestation), an
// initiating in-person visit recorded, AND a designated billing provider
// chosen (the counting anchor — without it no minute is billable). painGatesForMonth
// checks the same two facts (plus enrollment + minutes) but emits granular
// blockers; this boolean drives the Onboard "CPM record incomplete" chip so the
// UI and the gate never disagree on what "complete" means. P1.3 necessity is
// intentionally excluded: existing complete records must not become blocked.
function isPainRecordComplete(patient) {
  const pain = _raw(patient).pain || {};
  return !!(pain.consent && pain.consent.signed === true
    && pain.consent.durationOver3Months === true
    && pain.initiatingVisit && pain.initiatingVisit.date
    && pain.billingProvider && pain.billingProvider.id != null && pain.billingProvider.id !== '');
}

// Build the persisted CPM record (raw.pain) written at enrollment / backfill.
// ONE place owns this shape so the writer (Onboard) and the readers (gates,
// evidence, note block) never drift. `provider` is the chosen designated CPM
// billing practitioner ({ id, name }). When `initiatingVisitDate` is omitted —
// bulk enrollment, where the in-person visit is per-patient and can't be shared
// — the initiatingVisit is left ABSENT so month gates stay blocked until it is
// recorded via the Onboard backfill chip. Callers merge this over any existing
// raw.pain (`{ ...(raw.pain||{}), ...buildPainEnrollmentRecord(...) }`) so
// future sibling keys (assessments, durationAttested) survive. An omitted or
// blank necessity key must stay omitted so that raw-first merge also preserves
// any previously confirmed necessity instead of clobbering it.
function buildPainEnrollmentRecord(opts) {
  const o = opts || {};
  const prov = o.provider || {};
  const provId = prov.id != null && prov.id !== '' ? String(prov.id) : '';
  const rec = {
    consent: {
      signed: true, date: o.consentDate || '', mode: o.mode || 'verbal', disclosures: true,
      // HARD requirement (plan P0.2): chronic pain persisting (or expected to
      // persist) >3 months, attested by the confirming operator at enrollment.
      durationOver3Months: o.durationOver3Months === true,
    },
    billingProvider: { id: provId, name: prov.name || '' },
  };
  if (o.initiatingVisitDate) {
    rec.initiatingVisit = { date: o.initiatingVisitDate, practitionerName: prov.name || '', practitionerId: provId };
  }
  // Newlines/control whitespace collapse to single spaces so pasted text can
  // never inject an extra apparent attestation line into the billing note.
  const necessityText = typeof o.necessity === 'string' ? o.necessity.replace(/\s+/g, ' ').trim() : '';
  // confirmedBy is the ACTING user who reviewed the text in the dialog — not
  // the designated billing provider, who may never have seen it. Without an
  // identified confirmer the text is not stored (and the note omits it).
  const confirmer = o.confirmedBy && (o.confirmedBy.name || o.confirmedBy.id != null) ? o.confirmedBy : null;
  if (necessityText && confirmer) {
    rec.necessity = {
      text: necessityText.slice(0, 600),
      confirmedBy: { id: confirmer.id != null ? String(confirmer.id) : '', name: confirmer.name || '' },
      date: o.consentDate || '',
    };
  }
  // Treatments deliberately excluded from the plan + rationale (P2.1). Same
  // rules as necessity: confirmer required to store, whitespace collapsed so
  // pasted text can't inject lines, blank key omitted so raw-first merge
  // preserves a previously confirmed record instead of clobbering it. The
  // care-plan prompt renders exclusions ONLY from this stored field.
  const excludedText = typeof o.excludedTreatments === 'string' ? o.excludedTreatments.replace(/\s+/g, ' ').trim() : '';
  if (excludedText && confirmer) {
    rec.excludedTreatments = {
      text: excludedText.slice(0, 600),
      confirmedBy: { id: confirmer.id != null ? String(confirmer.id) : '', name: confirmer.name || '' },
      date: o.consentDate || '',
    };
  }
  return rec;
}

// Month gates for billing G3002. opts.scaleOverride demotes ONLY the missing
// validated-scale condition to a warning after explicit operator acknowledgement;
// enrollment, record completeness, and designated-provider minutes stay hard.
function painGatesForMonth(patient, monthStr, staffList, opts) {
  const raw = _raw(patient);
  const blockers = [];
  const warnings = [];
  if (!isPainEnrolledPatient(raw)) blockers.push('not enrolled in pain management (billing.painProgram)');
  // Dedicated CPM consent (disclosures included) — global CCM consent no
  // longer gates CPM (P0.2). Legacy enrollees fail pairing (base CCM/APCM
  // billing is unaffected) until the CPM record is completed on Onboard.
  const painRec = raw.pain || {};
  if (!(painRec.consent && painRec.consent.signed === true)) {
    blockers.push('CPM consent record incomplete — complete the CPM record (consent + initiating visit + billing provider) on Onboard');
  }
  if (!(painRec.consent && painRec.consent.durationOver3Months === true)) {
    blockers.push('chronic-pain duration (>3 months) not attested — re-save the CPM record on Onboard');
  }
  if (!(painRec.initiatingVisit && painRec.initiatingVisit.date)) {
    blockers.push('initiating in-person visit (date + practitioner) not recorded');
  }
  const ev = cpmMonthlyEvidence(raw, monthStr, staffList);
  if (!ev.designatedProviderId) {
    blockers.push('designated CPM billing provider not recorded — no minute can count until one is chosen');
  }
  const minutes = ev.billableProviderMinutes;
  if (minutes < PAIN_BASE_TARGET_MIN) {
    blockers.push('designated-provider CPM time below 30 min (' + ev.billableProviderMinutes + 'm billable of '
      + ev.totalPainMinutes + 'm pain-tagged — staff/other-provider/unattributed time never counts)');
  }
  // Only well-formed catalog assessments satisfy the gate — a malformed or
  // unsupported-scale record must not slip past the acknowledgement flow.
  const scaleCount = painAssessmentsForMonth(raw, monthStr).filter(isValidPainAssessment).length;
  if (!scaleCount) {
    if (opts && opts.scaleOverride === true) {
      warnings.push('No validated pain scale administered this month (acknowledged by operator)');
    } else {
      blockers.push('no validated pain scale (NRS) administered this month — record one on the Pain lens, or acknowledge at generate/push to bill without it');
    }
  }
  return { ok: blockers.length === 0, blockers, warnings, minutes, evidence: ev };
}

// CPT lines for a gates-passing month: G3002 plus one G3003 per additional
// full 15 minutes, capped at 4 units.
function resolvePainCodes(patient, monthStr, rateDescs, staffList) {
  const descs = rateDescs || {};
  // Same evidence the gate uses — G3003 units can never come from minutes the
  // gate would reject (codex finding 4).
  const minutes = cpmBillableMinutes(patient, monthStr, staffList);
  const codes = [{
    code: PAIN_BASE_CODE,
    unit: 1,
    desc: descs[PAIN_BASE_CODE] || 'Chronic pain management, first 30 min/month',
    target: PAIN_BASE_TARGET_MIN,
    met: minutes >= PAIN_BASE_TARGET_MIN,
  }];
  const units = Math.min(Math.floor(Math.max(0, minutes - PAIN_BASE_TARGET_MIN) / PAIN_ADDON_UNIT_MIN), PAIN_ADDON_MAX_UNITS);
  for (let u = 1; u <= units; u++) {
    codes.push({
      code: PAIN_ADDON_CODE,
      unit: u,
      desc: descs[PAIN_ADDON_CODE] || 'Chronic pain management, each addl 15 min',
      target: PAIN_ADDON_UNIT_MIN,
      met: true,
    });
  }
  return codes;
}

// Deterministic note block appended to the combined billing note when the
// month's bill carries G3002 — same role as buildBhiAddonAttestationLines
// for G0570. States only what the chart evidences: the pain-family time and
// the practitioner-time framing the code requires. No AI prose.
function buildPainAttestationLines(patient, monthStr, staffList, formatEntryStaff) {
  const raw = _raw(patient);
  const ev = cpmMonthlyEvidence(raw, monthStr, staffList);
  const lines = [];
  lines.push('CHRONIC PAIN MANAGEMENT (G3002):');
  // Persisted CPM record (P0.2) — data-backed lines, omit-if-absent: each
  // line states a RECORDED fact with its specifics; a missing field silently
  // drops the line (or clause), never a softened guess.
  const painRec = raw.pain || {};
  const iv = painRec.initiatingVisit || {};
  if (iv.date) {
    lines.push('- Initiating in-person visit: ' + iv.date + (iv.practitionerName ? ' by ' + iv.practitionerName : '') + '.');
  }
  const consent = painRec.consent || {};
  if (consent.signed === true && consent.date) {
    // Parenthetical built ONLY from recorded fields (codex 8e review): no
    // default mode — an unrecorded mode must not become an asserted "verbal" —
    // and disclosures wording only on a strict boolean true.
    const consentBits = [];
    if (consent.mode) consentBits.push(consent.mode);
    if (consent.disclosures === true) consentBits.push('incl. coinsurance + right-to-discontinue disclosures');
    lines.push('- CPM consent' + (consentBits.length ? ' (' + consentBits.join(', ') + ')' : '')
      + ' obtained ' + consent.date + '.');
  }
  if (consent.durationOver3Months === true) {
    lines.push('- Chronic pain persisting (or expected to persist) longer than 3 months was attested at enrollment.');
  }
  // Confirmation metadata is REQUIRED, not decorative: an unconfirmed draft
  // (legacy/imported {text} with no confirmer) must never reach a signed note.
  if (painRec.necessity && painRec.necessity.text
      && painRec.necessity.confirmedBy && painRec.necessity.confirmedBy.name) {
    const necessity = painRec.necessity;
    lines.push('- Medical necessity: ' + necessity.text
      + ' (confirmed by ' + necessity.confirmedBy.name + (necessity.date ? ', ' + necessity.date : '') + ').');
  }
  // In-month structured assessments only. Every rendered fragment comes from
  // the stored record; only catalog-valid records (same predicate as the
  // gate) may be asserted as a "Validated pain scale".
  for (const assessment of painAssessmentsForMonth(raw, monthStr)) {
    if (!isValidPainAssessment(assessment)) continue;
    const scale = lookupPainScale(assessment.scale);
    const max = assessment.scale === 'NRS' && scale ? '/' + scale.maxScore : '';
    const severity = assessment.severity ? ' (' + assessment.severity + ')' : '';
    lines.push('- Validated pain scale: ' + assessment.scale + ' ' + assessment.score + max + severity + ' on ' + assessment.date + '.');
  }
  const entries = (Array.isArray(raw.activityLog) ? raw.activityLog : [])
    .filter((e) => e && !e.isConsolidatedNote && (e.date || '').startsWith(monthStr || '') && entryProgramFamily(e) === 'pain')
    .sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  // Only chart-backed statements here (compliance review 2026-07-07): the
  // person-centered pain care plan and per-entry practitioner verification
  // don't exist as data yet (CPM-BILLING-COMPLIANCE-PLAN.md P0.1/P2.1), so
  // this block documents the minutes + itemized log and binds the
  // practitioner-furnishing requirement to the SIGNATURE — the signing
  // practitioner attests it, the system does not assert it.
  // The minutes shown are the SAME billable minutes the G3002/G3003 codes
  // resolve from — the note can never claim more time than the bill counted —
  // and the excluded split is documented.
  {
    const excluded = ev.otherMinutes + ev.unknownMinutes;
    lines.push('- Pain-management time documented this month: ' + ev.billableProviderMinutes
      + ' minutes personally furnished by the designated billing provider, distinct from any CCM/BHI time (furnishing staff itemized below).'
      + (excluded > 0 ? ' Excluded from the billable total: ' + excluded + ' minutes of pain-tagged staff/other-provider/unknown-credential time.' : ''));
  }
  // Only stored, validated modes support a modality statement. Missing or
  // invalid values remain unspecified and never become an inferred mode.
  const modalityCounts = { telehealth: 0, 'in-person': 0 };
  let unspecifiedModes = 0;
  for (const e of entries) {
    const mode = _painDeliveryMode(e);
    if (mode) modalityCounts[mode] += 1;
    else unspecifiedModes += 1;
  }
  const recordedModes = modalityCounts.telehealth + modalityCounts['in-person'];
  if (recordedModes > 0) {
    const summary = [];
    for (const mode of ['telehealth', 'in-person']) {
      const count = modalityCounts[mode];
      if (count > 0) summary.push(mode + ' (' + count + ' ' + (count === 1 ? 'entry' : 'entries') + ')');
    }
    if (unspecifiedModes > 0) {
      summary.push(unspecifiedModes + ' ' + (unspecifiedModes === 1 ? 'entry' : 'entries') + ' unspecified');
    }
    lines.push('- Delivery modality this month: ' + summary.join(', ') + '.');
  }
  lines.push('- By signing, the billing practitioner attests the CPM time above was personally furnished by the billing practitioner/QHP as required for G3002/G3003.');
  if (entries.length) {
    lines.push('Itemized pain-management time log:');
    for (const e of entries) {
      const dur = parseInt(e.duration, 10) || 0;
      const type = e.type || e.label || 'Pain management';
      const staffStr = typeof formatEntryStaff === 'function' ? formatEntryStaff(e, staffList) : (e.staff || '');
      const note = (e.notes || e.desc || '').trim();
      const mode = _painDeliveryMode(e);
      lines.push('- ' + (staffStr ? staffStr + ' — ' : '') + dur + ' min — ' + type
        + (note ? ' — ' + note : '') + (mode ? ' — ' + mode : ''));
    }
  }
  return lines;
}

module.exports = {
  CPM_BILLING_CREDENTIALS, cpmMonthlyEvidence, cpmBillableMinutes,
  buildPainNecessityDraft, buildPainEnrollmentRecord, isPainRecordComplete,
  PAIN_BASE_CODE,
  PAIN_ADDON_CODE,
  PAIN_BASE_TARGET_MIN,
  PAIN_ADDON_UNIT_MIN,
  PAIN_ADDON_MAX_UNITS,
  isPainEnrolledPatient,
  painMonthlyMinutes,
  painGatesForMonth,
  resolvePainCodes,
  buildPainAttestationLines,
};
